import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent } from './ui/dialog';
import { Share2, Copy, Trash2, ExternalLink, X, AlertTriangle, Pencil } from 'lucide-react';
import ModalCloseButton from './ModalCloseButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharePortfolioModal({ open, onClose }) {
  const [existing, setExisting] = useState(null);
  const [realName, setRealName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDollar, setShowDollar] = useState(false);
  const [hideTaxAdv, setHideTaxAdv] = useState(false);
  const [showYoc, setShowYoc] = useState(false);
  const [showGain, setShowGain] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    // On modal open: fetch current share state. If the user already has a
    // share URL, the snapshot on the public page is auto-refreshed below
    // (via createOrRefresh) so the shared portfolio ALWAYS reflects the
    // latest positions without the user having to click anything.
    axios.get(`${API}/me/share`, { withCredentials: true })
      .then(async (res) => {
        setRealName(res.data?.real_name || '');
        if (res.data?.has_share) {
          setExisting(res.data);
          setShowDollar(!!res.data.show_dollar_amounts);
          setHideTaxAdv(!!res.data.hide_tax_advantaged);
          setShowYoc(!!res.data.show_yield_on_cost);
          setShowGain(!!res.data.show_capital_gain);
          setDisplayName(res.data.display_name || res.data.real_name || '');
          // Silently refresh the snapshot so the already-shared URL
          // reflects today's portfolio. Slug stays the same (backend reuses it).
          try {
            const { data } = await axios.post(
              `${API}/me/share`,
              {
                show_dollar_amounts: !!res.data.show_dollar_amounts,
                hide_tax_advantaged: !!res.data.hide_tax_advantaged,
                show_yield_on_cost: !!res.data.show_yield_on_cost,
                show_capital_gain: !!res.data.show_capital_gain,
                display_name: (res.data.display_name || '').trim(),
              },
              { withCredentials: true },
            );
            setExisting({ has_share: true, ...data, real_name: res.data.real_name });
          } catch {}
        } else {
          setExisting(null);
          setDisplayName(res.data?.real_name || '');
        }
      })
      .catch(() => setExisting(null))
      .finally(() => setLoading(false));
    setConfirmDelete(false);
    setEditingName(false);
    setCopied(false);
  }, [open]);

  // Round 95x — Use the backend OG-rich wrapper as the canonical
  // share URL. When pasted into Discord / Slack / iMessage / X, the
  // unfurler bot fetches this URL and sees per-portfolio Open Graph
  // tags (owner name, yield, monthly income) → rich preview card.
  // Real browsers follow the 0-second meta-refresh inside that page
  // straight to `/p/{slug}` where the React SPA renders normally,
  // so humans never notice the wrapper.
  const shareUrl = existing?.slug
    ? `${window.location.origin}/api/share/og/${existing.slug}`
    : '';

  const createOrRefresh = async () => {
    setLoading(true);
    try {
      const payload = {
        show_dollar_amounts: showDollar,
        hide_tax_advantaged: hideTaxAdv,
        show_yield_on_cost: showYoc,
        show_capital_gain: showGain,
        display_name: (displayName || '').trim(),
      };
      const { data } = await axios.post(`${API}/me/share`, payload, { withCredentials: true });
      setExisting({ has_share: true, ...data, real_name: realName });
      setEditingName(false);
    } catch { /* noop */ }
    setLoading(false);
  };

  const deleteShare = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/me/share`, { withCredentials: true });
      setExisting(null);
      setConfirmDelete(false);
    } catch { /* noop */ }
    setLoading(false);
  };

  const copyUrl = () => {
    if (!shareUrl) return;
    const onSuccess = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success('Share link copied to clipboard', {
        description: 'Paste it anywhere to share your portfolio snapshot.',
      });
    };
    const onFail = () => {
      toast.error("Couldn't copy to clipboard", {
        description: 'Your browser blocked the copy — try selecting the URL manually.',
      });
    };
    // The Clipboard API requires a secure context + top-level frame. In iframed
    // preview environments it throws NotAllowedError, so fall back to the
    // classic textarea+execCommand trick which works universally.
    const fallback = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '0';
        ta.style.left = '0';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, ta.value.length);
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) onSuccess();
        else onFail();
      } catch {
        onFail();
      }
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(shareUrl).then(onSuccess).catch(fallback);
    } else {
      fallback();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden [&>button]:hidden" data-testid="share-portfolio-modal">
        <div className="border-b px-5 py-3 flex items-center gap-2">
          <Share2 className="w-5 h-5 text-violet-500" />
          <h3 className="text-base font-bold flex-1">Share your portfolio</h3>
          <ModalCloseButton onClick={onClose} testId="share-modal-close-btn" />
        </div>

        <div className="px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : existing ? (
            <>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Public share URL</label>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    onClick={(e) => e.target.select()}
                    className="flex-1 px-3 py-2 text-xs font-mono rounded border bg-muted/40 min-w-0"
                    data-testid="share-url-input"
                  />
                  <button
                    onClick={copyUrl}
                    className="px-2.5 py-2 rounded-md text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 inline-flex items-center gap-1.5"
                    data-testid="share-copy-btn"
                    title="Copy URL"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  Snapshot updated just now. This URL stays the same forever —
                  it auto-refreshes every time you open this dialog.
                </p>
              </div>

              {/* Current display name + Change button */}
              <div className="rounded-md border bg-muted/20 px-3 py-2 flex items-center justify-between gap-2" data-testid="share-current-name">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Display name on share page</p>
                  <p className="text-sm font-bold truncate">{existing.display_name || realName || 'A DiviDrip investor'}</p>
                </div>
                <button
                  onClick={() => { setEditingName(true); setDisplayName(existing.display_name || realName || ''); }}
                  className="shrink-0 px-2.5 py-1 rounded text-xs font-semibold bg-muted hover:bg-muted/70 inline-flex items-center gap-1"
                  data-testid="share-change-name-btn"
                >
                  <Pencil className="w-3 h-3" /> Change
                </button>
              </div>

              {editingName && (
                <div className="space-y-2" data-testid="share-name-edit-form">
                  <label className="block text-xs font-semibold text-muted-foreground">New display name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={60}
                    className="w-full px-3 py-2 text-sm rounded border bg-background"
                    placeholder="A nickname, first name, or initials"
                    data-testid="share-display-name-input"
                    autoFocus
                  />
                  <p className="text-[10px] text-muted-foreground">
                    For your privacy, we recommend a nickname rather than your full name. Max 60 characters.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setEditingName(false); setDisplayName(existing.display_name || realName || ''); }}
                      className="flex-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-muted hover:bg-muted/70"
                      data-testid="share-name-cancel-btn"
                    >Cancel</button>
                    <button
                      onClick={createOrRefresh}
                      disabled={loading}
                      className="flex-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60"
                      data-testid="share-name-save-btn"
                    >{loading ? 'Saving...' : 'Save Name'}</button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 pt-2 border-t">
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-3 py-2 rounded-md text-xs font-semibold bg-muted hover:bg-muted/70 inline-flex items-center justify-center gap-1.5 text-foreground"
                  data-testid="share-open-btn"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Preview
                </a>
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="px-3 py-2 rounded-md text-xs font-semibold bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 hover:bg-red-100 inline-flex items-center gap-1.5"
                  data-testid="share-delete-btn"
                  title="Delete share"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Show-dollars toggle — changing this re-snaps with the same slug */}
              <label className="flex items-center gap-2 cursor-pointer text-xs pt-2" data-testid="share-show-dollars-wrapper">
                <input
                  type="checkbox"
                  checked={showDollar}
                  onChange={async (e) => {
                    const v = e.target.checked;
                    setShowDollar(v);
                    // Persist immediately — same slug, updated setting + snapshot
                    try {
                      const { data } = await axios.post(
                        `${API}/me/share`,
                        {
                          show_dollar_amounts: v,
                          hide_tax_advantaged: hideTaxAdv,
                          show_yield_on_cost: showYoc,
                          show_capital_gain: showGain,
                          display_name: (displayName || '').trim(),
                        },
                        { withCredentials: true },
                      );
                      setExisting({ has_share: true, ...data, real_name: realName });
                    } catch {}
                  }}
                  className="rounded"
                  data-testid="share-show-dollars-toggle"
                />
                <span>Show dollar amounts (uncheck to share only percentages &amp; yields)</span>
              </label>

              {/* Round 92o++++++++f (Feb 2026) — Hide tax-advantaged
                  account breakdown on the public share page. */}
              <label className="flex items-center gap-2 cursor-pointer text-xs pt-1" data-testid="share-hide-tax-advantaged-wrapper">
                <input
                  type="checkbox"
                  checked={hideTaxAdv}
                  onChange={async (e) => {
                    const v = e.target.checked;
                    setHideTaxAdv(v);
                    try {
                      const { data } = await axios.post(
                        `${API}/me/share`,
                        {
                          show_dollar_amounts: showDollar,
                          hide_tax_advantaged: v,
                          show_yield_on_cost: showYoc,
                          show_capital_gain: showGain,
                          display_name: (displayName || '').trim(),
                        },
                        { withCredentials: true },
                      );
                      setExisting({ has_share: true, ...data, real_name: realName });
                    } catch {}
                  }}
                  className="rounded"
                  data-testid="share-hide-tax-advantaged-toggle"
                />
                <span>Hide account-type allocation (keeps your Roth / IRA / taxable mix private)</span>
              </label>

              {/* Round 95x (Jun 2026) — Optional Yield-on-Cost card on
                  the public page. Ratio-only metric so it doesn't
                  require dollars-visible to be useful, but it does
                  hint at how long the owner has held (lower current
                  yield + high YoC = bought when price was lower) so
                  it's opt-in and OFF by default. */}
              <label className="flex items-center gap-2 cursor-pointer text-xs pt-1" data-testid="share-show-yoc-wrapper">
                <input
                  type="checkbox"
                  checked={showYoc}
                  onChange={async (e) => {
                    const v = e.target.checked;
                    setShowYoc(v);
                    try {
                      const { data } = await axios.post(
                        `${API}/me/share`,
                        {
                          show_dollar_amounts: showDollar,
                          hide_tax_advantaged: hideTaxAdv,
                          show_yield_on_cost: v,
                          show_capital_gain: showGain,
                          display_name: (displayName || '').trim(),
                        },
                        { withCredentials: true },
                      );
                      setExisting({ has_share: true, ...data, real_name: realName });
                    } catch {}
                  }}
                  className="rounded"
                  data-testid="share-show-yoc-toggle"
                />
                <span>Show Yield on Cost (annual divs ÷ your cost basis — hints at how long you&apos;ve held)</span>
              </label>

              {/* Round 95x (Jun 2026) — Optional Net Capital Gain
                  ("Unrealized Gain") card on the public page. Gated
                  on show_dollar_amounts because it's a dollar number
                  that would imply the magnitude of the portfolio if
                  exposed alone. Default OFF. */}
              <label
                className={`flex items-center gap-2 text-xs pt-1 ${showDollar ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                data-testid="share-show-gain-wrapper"
                title={showDollar ? '' : 'Enable "Show dollar amounts" first — capital gain is a dollar figure.'}
              >
                <input
                  type="checkbox"
                  checked={showGain && showDollar}
                  disabled={!showDollar}
                  onChange={async (e) => {
                    const v = e.target.checked;
                    setShowGain(v);
                    try {
                      const { data } = await axios.post(
                        `${API}/me/share`,
                        {
                          show_dollar_amounts: showDollar,
                          hide_tax_advantaged: hideTaxAdv,
                          show_yield_on_cost: showYoc,
                          show_capital_gain: v,
                          display_name: (displayName || '').trim(),
                        },
                        { withCredentials: true },
                      );
                      setExisting({ has_share: true, ...data, real_name: realName });
                    } catch {}
                  }}
                  className="rounded"
                  data-testid="share-show-gain-toggle"
                />
                <span>Show Unrealized Capital Gain (lifetime $ gain on remaining positions — requires dollar amounts)</span>
              </label>

              {confirmDelete && (
                <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 space-y-2" data-testid="share-delete-confirm">
                  <div className="flex gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <p className="font-bold text-red-900 dark:text-red-200 mb-1">Delete your share link?</p>
                      <p className="text-red-800 dark:text-red-300">
                        The URL <span className="font-mono">{shareUrl}</span> will stop working. Anyone who bookmarked it will see a "not found" page.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="flex-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-muted hover:bg-muted/70"
                      data-testid="share-delete-cancel-btn"
                    >Cancel</button>
                    <button
                      onClick={deleteShare}
                      disabled={loading}
                      className="flex-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                      data-testid="share-delete-confirm-btn"
                    >{loading ? 'Deleting...' : 'Delete Link'}</button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <p className="text-sm">
                Create a public read-only link to your portfolio. Great for sharing with friends, on social media, or in dividend investing communities.
              </p>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Display name on share page</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={60}
                  className="w-full px-3 py-2 text-sm rounded border bg-background"
                  placeholder="A nickname, first name, or initials"
                  data-testid="share-display-name-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  {realName
                    ? <>Pre-filled with your account name. <strong>For privacy we recommend changing it to a nickname</strong> — your real name can be looked up online.</>
                    : 'Choose any name or nickname — this is what visitors will see at the top of your share page.'}
                </p>
              </div>

              <label className="flex items-center gap-2 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={showDollar}
                  onChange={(e) => setShowDollar(e.target.checked)}
                  className="rounded"
                  data-testid="share-show-dollars-toggle"
                />
                <span>Show dollar amounts (uncheck to share only percentages &amp; yields)</span>
              </label>

              <button
                onClick={createOrRefresh}
                className="w-full px-3 py-2 rounded-md text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 inline-flex items-center justify-center gap-2"
                data-testid="share-create-btn"
              >
                <Share2 className="w-4 h-4" />
                Create Share Link
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
